export const API_BASE_URL = import.meta.env.VITE_API_URL || (['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'http://localhost:4009/api' : 'https://crmdemo.inplsoftwares.com/api');

export const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = {
        ...options.headers
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
        ...options,
        headers,
        credentials: 'include'
    };

    const response = await fetch(url, config);

    if (response.status === 401 && !url.includes('/logout')) {
        window.dispatchEvent(new Event('auth:unauthorized'));
    }

    return response;
};

export const authService = {
    async signup(username, email, password, role) {
        const response = await fetchWithAuth(`${API_BASE_URL}/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, email, password, role })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Signup failed');
        if (data.data?.token) localStorage.setItem('token', data.data.token);
        return data;
    },

    async login(email, password) {
        const response = await fetchWithAuth(`${API_BASE_URL}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Login failed');
        if (data.data?.token) {
            localStorage.setItem('token', data.data.token);
            // Notify hooks (e.g. useAppSettings) that auth is now available
            window.dispatchEvent(new Event('auth:login'));
        }
        return data;
    },

    async logout() {
        const token = localStorage.getItem('token');
        const response = await fetchWithAuth(`${API_BASE_URL}/logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        localStorage.removeItem('token');
        return response.json();
    },

    async changePassword(currentPassword, newPassword) {
        const response = await fetchWithAuth(`${API_BASE_URL}/change-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ currentPassword, newPassword })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to change password');
        return data;
    },

    getToken() {
        return localStorage.getItem('token');
    },

    isAuthenticated() {
        return !!localStorage.getItem('token');
    }
};

export const contactService = {
    async getAll(paramsObj = {}) {
        const { page = 1, limit = 50, search = '', source = '' } = paramsObj;
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit.toString());
        if (search) params.append('search', search);
        if (source && source !== 'All') params.append('source', source);

        const url = `${API_BASE_URL}/contacts${params.toString() ? '?' + params.toString() : ''}`;
        const response = await fetchWithAuth(url, {
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch contacts');
        return {
            data: data.data || [],
            pagination: data.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 }
        };
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contacts/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch contact');
        return data.data;
    },

    async create(contactData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contacts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contactData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create contact');
        return data.data;
    },

    async update(id, contactData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contacts/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contactData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update contact');
        return data.data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contacts/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete contact');
        return data;
    },

    async deleteAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/contacts`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete all contacts');
        return data;
    },

    async import(contactsData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contacts/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contacts: contactsData })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to import contacts');
        return data;
    }
};

export const sourcesService = {
    async getAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch sources');
        return data.data || [];
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch source');
        return data.data;
    },

    async create(name) {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create source');
        return data.data;
    },

    async update(id, name) {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update source');
        return data.data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete source');
        return data;
    },

    async getSourceLeads(id, params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/sources/${id}/leads?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch source leads');
        return data;
    }
};

export const leadsService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/leads?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch leads');
        return data;
    },

    async getStats() {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/stats`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch leads stats');
        return data.data;
    },

    async getAnalytics(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/analytics?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch leads analytics');
        return data.data;
    },

    async getCalendarTentative() {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/calendar-tentative`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch tentative leads');
        return data.data || [];
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch lead');
        return data.data;
    },

    async create(leadData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create lead');
        return data.data;
    },

    async update(id, leadData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update lead');
        return data.data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete lead');
        return data;
    },

    async deleteAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/all`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete all leads');
        return data;
    },

    async import(leadsData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ leads: leadsData })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to import leads');
        return data;
    },

    async addNote(id, noteData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/${id}/timeline`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(noteData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to add note');
        return data.data;
    },

    async getTimeline(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/${id}/timeline`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch timeline');
        return data.data;
    },

    async addPayment(id, paymentData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/${id}/payments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(paymentData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to add payment');
        return data; // Return full response { status, message, data }
    },

    async deletePayment(id, paymentId) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/${id}/payments/${paymentId}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete payment');
        return data;
    },

    async exportFinance() {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/export/finance`);
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to export finance data');
        }
        return response; // Return response to get blob in the component
    },

    async logCall(id, callData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/leads/${id}/call-log`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(callData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to log call');
        return data.data;
    }
};

export const userService = {
    async getAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/users`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch users');
        return data.data;
    },

    async invite(userData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to invite user');
        return data; // returns full response { status, message, data: { user, tempPassword, resetLink } }
    },

    async update(userData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update user');
        return data; // returns full response { status, message, data: { ... } }
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete user');
        return data;
    },

    async resetPassword(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${id}/reset-password`, {
            method: 'POST'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to reset password');
        return data; // returns { status, data: { tempPassword } }
    },

    async changePassword(id, newPassword) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${id}/password`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newPassword })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to change password');
        return data;
    },

    async toggleActive(id, is_active) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${id}/toggle-active`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_active })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update user status');
        return data;
    }
};

export const settingsService = {
    async get(key) {
        const response = await fetchWithAuth(`${API_BASE_URL}/settings/${key}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch settings');
        return data.data;
    },

    async update(key, value) {
        const response = await fetchWithAuth(`${API_BASE_URL}/settings/${key}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update settings');
        return data.data;
    }
};

export const maintenanceService = {
    async resetDatabase() {
        const response = await fetchWithAuth(`${API_BASE_URL}/maintenance/reset`, {
            method: 'POST'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to reset database');
        return data;
    },

    async migrateContacts() {
        const response = await fetchWithAuth(`${API_BASE_URL}/maintenance/migrate-contacts`, {
            method: 'POST'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to migrate contacts');
        return data;
    },

    async consolidateSources() {
        const response = await fetchWithAuth(`${API_BASE_URL}/maintenance/consolidate-sources`, {
            method: 'POST'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to consolidate sources');
        return data;
    }
};

export const notificationsService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch notifications');
        return data; // returns { data, totalCount }
    },

    async markAsRead(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications/${id}/read`, {
            method: 'PATCH'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to mark notification as read');
        return data;
    },

    async markAllAsRead(userData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications/read-all`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to mark all as read');
        return data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete notification');
        return data;
    }
};

export const whatsappService = {
    async getConversations(page = 1, limit = 50) {
        const response = await fetchWithAuth(`${API_BASE_URL}/whatsapp/conversations?page=${page}&limit=${limit}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch conversations');
        // Return full object so frontend can access pagination info
        return { data: data.data, pagination: data.pagination };
    },

    async getMessages(leadId) {
        const response = await fetchWithAuth(`${API_BASE_URL}/whatsapp/messages/${leadId}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch messages');
        return data.data;
    },

    async sendMessage(to, message, leadId, media = null) {
        const body = { to, message, leadId };
        if (media) {
            body.mediaBase64 = media.base64;
            body.mediaMime = media.mimeType;
            body.mediaName = media.name;
        }
        const response = await fetchWithAuth(`${API_BASE_URL}/whatsapp/send`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to send message');
        return data.data;
    },

    async editMessage(messageId, newText, to, leadId) {
        const response = await fetchWithAuth(`${API_BASE_URL}/whatsapp/edit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messageId, newText, to, leadId })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to edit message');
        return data.data;
    },

    async reactToMessage(messageId, emoji, to, leadId) {
        const response = await fetchWithAuth(`${API_BASE_URL}/whatsapp/react`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messageId, emoji, to, leadId })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to react to message');
        return data.data;
    },

    async deleteMessage(messageId, leadId) {
        const response = await fetchWithAuth(`${API_BASE_URL}/whatsapp/message/${encodeURIComponent(messageId)}?leadId=${leadId}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete message');
        return data.data;
    }
};

export const auditService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/audit-logs?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch audit logs');
        return data; // returns { status, data, meta }
    }
};

export const bookingService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/bookings?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch bookings');
        return data; // returns { status, data, meta }
    },
    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/bookings/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch booking');
        return data.data;
    }
};

export const conversationService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/conversations?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch conversations');
        return data; // returns { status, data, meta }
    },
    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/conversations/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch conversation');
        return data.data;
    }
};

export const facebookService = {
    async getAuthUrl() {
        const response = await fetchWithAuth(`${API_BASE_URL}/facebook/auth-url`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to get Facebook auth URL');
        return data.data;
    },

    async getStatus() {
        const response = await fetchWithAuth(`${API_BASE_URL}/facebook/status`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch Facebook status');
        return data.data;
    },

    async disconnect(pageId) {
        const response = await fetchWithAuth(`${API_BASE_URL}/facebook/disconnect`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pageId })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to disconnect Facebook page');
        return data;
    }
};

export default authService;
